import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import API_URL from "../config";

export default function Home() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [stats, setStats] = useState({ students_certified: 0, certificates_issued: 0, partner_colleges: 0 });
  const [displayCounts, setDisplayCounts] = useState({ students_certified: 0, certificates_issued: 0, partner_colleges: 0 });
  const [students, setStudents] = useState([]);
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);

  useEffect(() => {
    setVisible(true);
  }, []);

  // Fetch real stats from backend
  useEffect(() => {
    fetch(`${API_URL}/stats`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setStats(data.stats);
        }
      })
      .catch(err => console.error("Failed to fetch stats:", err));
  }, []);

  // Fetch scrolling students
  useEffect(() => {
    fetch(`${API_URL}/students-scroll`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.students) {
          setStudents(data.students);
        }
      })
      .catch(err => console.error("Failed to fetch students:", err));
  }, []);

  // Intersection observer for stats section
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
        }
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  // Animate counts when stats become visible
  useEffect(() => {
    if (!statsVisible) return;
    const duration = 2000;
    const start = Date.now();

    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setDisplayCounts({
        students_certified: Math.floor(ease * stats.students_certified),
        certificates_issued: Math.floor(ease * stats.certificates_issued),
        partner_colleges: Math.floor(ease * stats.partner_colleges),
      });
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [statsVisible, stats]);

  // Auto-scroll through students
  useEffect(() => {
    if (students.length === 0) return;
    const interval = setInterval(() => {
      setCurrentStudentIndex(prev => (prev + 1) % students.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [students.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      {/* HERO SECTION */}
      <div className="relative overflow-hidden">
        {/* Animated background particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-indigo-200/20 animate-float"
              style={{
                width: `${Math.random() * 8 + 4}px`,
                height: `${Math.random() * 8 + 4}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 8 + 6}s`,
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-4 pt-20 pb-16 relative z-10">
          <div
            className={`text-center max-w-4xl mx-auto transition-all duration-1000 transform ${
              visible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6 animate-pulse-slow">
              <span className="w-2 h-2 bg-indigo-500 rounded-full animate-ping" />
              Official Verification Portal
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-800 leading-tight">
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Official Internship
              </span>
              <br />
              <span className="text-slate-800">Certificate Verification</span>
            </h1>

            <p className="text-slate-500 text-lg sm:text-xl mt-8 max-w-2xl mx-auto leading-relaxed">
              SmartGoNext issues internship certificates to students who
              successfully complete its programs. Use this portal to verify
              authenticity and view certificate details using the certificate ID.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
              <button
                onClick={() => navigate("/verify")}
                className="group relative px-8 py-4 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl text-lg font-semibold hover:from-indigo-700 hover:to-indigo-800 transition-all duration-300 shadow-lg shadow-indigo-200 hover:shadow-xl hover:shadow-indigo-300 hover:-translate-y-0.5"
              >
                <span className="relative z-10 flex items-center gap-2">
                  🔍 Verify Certificate
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>

              <button
                onClick={() => navigate("/apply")}
                className="group relative px-8 py-4 bg-white border-2 border-indigo-200 text-indigo-700 rounded-xl text-lg font-semibold hover:border-indigo-400 hover:bg-indigo-50 transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5"
              >
                <span className="relative z-10 flex items-center gap-2">
                  📝 Apply for Internship
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>

              <button
                onClick={() => navigate("/admin/login")}
                className="px-8 py-4 border border-slate-300 text-slate-600 rounded-xl text-lg font-medium hover:bg-slate-100 hover:border-slate-400 transition-all duration-300"
              >
                Admin Login
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* STATS SECTION - Real DB Values */}
      <div ref={statsRef} className="max-w-5xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          <div
            className={`relative bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center transform transition-all duration-700 ${
              statsVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
            style={{ transitionDelay: "0ms" }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-2xl mb-4 shadow-lg">
              🎓
            </div>
            <div className="text-4xl font-bold text-slate-800 mb-2">
              {statsVisible ? displayCounts.students_certified.toLocaleString() : "0"}+
            </div>
            <div className="text-slate-500 font-medium">Students Certified</div>
          </div>

          <div
            className={`relative bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center transform transition-all duration-700 ${
              statsVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
            style={{ transitionDelay: "200ms" }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 text-white text-2xl mb-4 shadow-lg">
              📜
            </div>
            <div className="text-4xl font-bold text-slate-800 mb-2">
              {statsVisible ? displayCounts.certificates_issued.toLocaleString() : "0"}+
            </div>
            <div className="text-slate-500 font-medium">Certificates Issued</div>
          </div>

          <div
            className={`relative bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center transform transition-all duration-700 ${
              statsVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
            style={{ transitionDelay: "400ms" }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-2xl mb-4 shadow-lg">
              🏛️
            </div>
            <div className="text-4xl font-bold text-slate-800 mb-2">
              {statsVisible ? displayCounts.partner_colleges.toLocaleString() : "0"}+
            </div>
            <div className="text-slate-500 font-medium">Partner Colleges</div>
          </div>
        </div>
      </div>

      {/* SCROLLING STUDENT DETAILS SECTION */}
      <div className="max-w-5xl mx-auto px-4 pb-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-slate-800">Our Certified Students</h2>
          <p className="text-slate-500 mt-2">Scroll through recently certified students</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {students.length > 0 ? (
            <div className="relative">
              {/* Student card with fade transition */}
              {students.map((student, idx) => (
                <div
                  key={student.cert_id}
                  className={`p-6 sm:p-8 transition-all duration-700 ${
                    idx === currentStudentIndex
                      ? "opacity-100 translate-y-0 block"
                      : "opacity-0 translate-y-4 hidden"
                  }`}
                >
                  <div className="flex flex-col sm:flex-row items-center gap-6">
                    {/* Student Photo */}
                    <div className="flex-shrink-0">
                      {student.student_photo_url ? (
                        <img
                          src={student.student_photo_url}
                          alt={student.student_name}
                          className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-4 border-indigo-100 shadow-md"
                        />
                      ) : (
                        <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center border-4 border-indigo-100 shadow-md">
                          <span className="text-3xl font-bold text-indigo-400">
                            {student.student_name ? student.student_name.charAt(0).toUpperCase() : "?"}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Student Info */}
                    <div className="flex-1 text-center sm:text-left">
                      <h3 className="text-xl sm:text-2xl font-bold text-slate-800">
                        {student.student_name}
                      </h3>
                      <div className="mt-3 space-y-1.5 text-slate-500">
                        <p className="flex items-center gap-2 justify-center sm:justify-start">
                          <span className="text-indigo-500">🏛️</span>
                          <span>{student.college || "N/A"}</span>
                        </p>
                        <p className="flex items-center gap-2 justify-center sm:justify-start">
                          <span className="text-purple-500">📚</span>
                          <span>{student.department || "N/A"}</span>
                        </p>
                        <p className="flex items-center gap-2 justify-center sm:justify-start">
                          <span className="text-emerald-500">🆔</span>
                          <span className="font-mono text-sm text-slate-400">{student.cert_id}</span>
                        </p>
                        <p className="flex items-center gap-2 justify-center sm:justify-start">
                          <span className="text-amber-500">📅</span>
                          <span>Certified on: {student.created_at || "N/A"}</span>
                        </p>
                      </div>
                    </div>

                    {/* View Certificate Button */}
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => navigate(`/verify?cert_id=${student.cert_id}`)}
                        className="px-5 py-2.5 bg-indigo-50 text-indigo-700 rounded-lg font-medium hover:bg-indigo-100 transition-all duration-200 border border-indigo-200 whitespace-nowrap"
                      >
                        View Certificate →
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Navigation dots */}
              <div className="flex justify-center gap-2 pb-4">
                {students.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentStudentIndex(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      idx === currentStudentIndex
                        ? "bg-indigo-600 w-6"
                        : "bg-slate-300 hover:bg-slate-400"
                    }`}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="p-8 text-center text-slate-400">
              <div className="text-4xl mb-3">🎓</div>
              <p className="text-lg font-medium text-slate-500 mb-1">No certified students yet</p>
              <p className="text-sm">Certified students will appear here once approved</p>
            </div>
          )}
        </div>
      </div>

      {/* FEATURES SECTION */}
      <div className="max-w-7xl mx-auto px-4 pb-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800">How It Works</h2>
          <p className="text-slate-500 mt-2">Simple and transparent process for everyone</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              step: "01",
              title: "Apply for Internship",
              desc: "Students submit their application with details and photo. Admin reviews and accepts the application.",
              color: "indigo",
              icon: "📝",
            },
            {
              step: "02",
              title: "Complete Internship",
              desc: "After the internship period ends, admin approves and the certificate is automatically generated.",
              color: "purple",
              icon: "⏳",
            },
            {
              step: "03",
              title: "Verify & Share",
              desc: "Certificate is emailed to the student automatically. Anyone can verify it using the unique certificate ID.",
              color: "emerald",
              icon: "✅",
            },
          ].map((feature, i) => (
            <div
              key={i}
              className="group relative bg-white rounded-2xl border border-slate-200 shadow-sm p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="absolute top-4 right-4 text-6xl font-bold text-slate-100 select-none group-hover:text-indigo-100 transition-colors">
                {feature.step}
              </div>
              <div className="text-4xl mb-4">{feature.icon}</div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3 relative z-10">
                {feature.title}
              </h3>
              <p className="text-slate-500 leading-relaxed relative z-10">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA BANNER */}
      <div className="max-w-7xl mx-auto px-4 pb-20">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-3xl p-12 text-center text-white shadow-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-indigo-200 text-lg mb-8 max-w-xl mx-auto">
            Apply for an internship today and earn your official SmartGoNext certificate.
          </p>
          <button
            onClick={() => navigate("/apply")}
            className="px-10 py-4 bg-white text-indigo-700 rounded-xl font-semibold text-lg hover:bg-indigo-50 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          >
            Apply Now 🚀
          </button>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) scale(1); opacity: 0.2; }
          50% { transform: translateY(-20px) scale(1.2); opacity: 0.4; }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}